/**
 * Tema claro/escuro do app inteiro.
 * A escolha fica no navegador; sem escolha, vale o tema do sistema.
 * O script em `index.html` aplica a classe `dark` antes de a tela aparecer (sem piscar).
 */
import { useSyncExternalStore } from 'react'

export type EscolhaTema = 'claro' | 'escuro' | 'sistema'

const CHAVE = 'heimdall.tema'
const MUDANCA = 'heimdall:tema'

function lerEscolha(): EscolhaTema {
  try {
    const salvo = window.localStorage.getItem(CHAVE)
    if (salvo === 'claro' || salvo === 'escuro') return salvo
  } catch {
    // Sem armazenamento: segue o sistema.
  }
  return 'sistema'
}

function sistemaEscuro(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Liga ou desliga a classe `dark` no elemento raiz conforme a escolha. */
export function aplicarTema(escolha: EscolhaTema = lerEscolha()): void {
  const escuro = escolha === 'escuro' || (escolha === 'sistema' && sistemaEscuro())
  const raiz = document.documentElement
  raiz.classList.toggle('dark', escuro)
  raiz.style.colorScheme = escuro ? 'dark' : 'light'
}

export function escolherTema(escolha: EscolhaTema): void {
  try {
    if (escolha === 'sistema') window.localStorage.removeItem(CHAVE)
    else window.localStorage.setItem(CHAVE, escolha)
  } catch {
    // A escolha vale só até recarregar.
  }
  aplicarTema(escolha)
  window.dispatchEvent(new Event(MUDANCA))
}

/** Começa a seguir o sistema quando ele muda (só vale sem escolha salva). */
export function iniciarTema(): void {
  aplicarTema()
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => aplicarTema())
  window.addEventListener('storage', () => {
    aplicarTema()
    window.dispatchEvent(new Event(MUDANCA))
  })
}

function assinar(aviso: () => void): () => void {
  window.addEventListener(MUDANCA, aviso)
  return () => window.removeEventListener(MUDANCA, aviso)
}

/** Escolha atual e função para trocá-la. */
export function useTema(): { escolha: EscolhaTema; escuro: boolean; alternar: () => void } {
  const escolha = useSyncExternalStore(assinar, lerEscolha, () => 'sistema' as const)
  const escuro = useSyncExternalStore(
    assinar,
    () => document.documentElement.classList.contains('dark'),
    () => false
  )
  return { escolha, escuro, alternar: () => escolherTema(escuro ? 'claro' : 'escuro') }
}
